import { computed } from 'vue'

const DEFAULT_HEADER_HEIGHT = 48

const envInsets = {
  top: 'env(safe-area-inset-top, 0px)',
  right: 'env(safe-area-inset-right, 0px)',
  bottom: 'env(safe-area-inset-bottom, 0px)',
  left: 'env(safe-area-inset-left, 0px)',
}

// Floating mobile tab bar height — must match MobileTabBar's grid row (`h-16` = 64px).
export const FLOATING_TAB_BAR_HEIGHT = 64

// Bottom CSS offset for sheets that float above the tab bar. The extra 8px keeps a sliver of
// map between the sheet and the bar so the bar reads as a separate glass pill (not a fused card).
// Centralized here so every bottom sheet stays in lock-step instead of duplicating the string.
// Pass `tabBarPx = 0` to reclaim the tab bar's height when it is hidden (detail levels), dropping
// the sheet to just above the home indicator.
export function floatingSheetBottomOffset(tabBarPx = FLOATING_TAB_BAR_HEIGHT) {
  return `calc(max(${envInsets.bottom}, 10px) + ${tabBarPx}px + 8px)`
}

export const FLOATING_SHEET_BOTTOM_OFFSET = floatingSheetBottomOffset()

// Standard top inset for floating elements below the notch / Dynamic Island. 12px clears the
// Dynamic Island (~59pt), notch (~47–50pt) and flat status bar (~20pt) comfortably. Relies on
// `viewport-fit=cover` (set in index.html) for `env()` to resolve on-device.
export function safeAreaTopOffset(gapPx = 12) {
  return `calc(${envInsets.top} + ${gapPx}px)`
}

// CSS max-height for fixed mobile bottom sheets. The sheet's physical top edge is determined by
// height + bottom offset, so subtract both the top safe area and the active bottom anchor.
export function mobileSheetMaxHeight(bottomOffset = '0px', topGapPx = 8) {
  return `calc(100dvh - ${envInsets.top} - ${topGapPx}px - ${bottomOffset})`
}

// Read the actual top safe-area inset (notch / Dynamic Island) in pixels. `env()` is CSS-only, so
// measure it via a hidden probe whose padding is driven by `env(safe-area-inset-top)`. Resolves to
// ~47–59px on notched iPhones (with viewport-fit=cover), 0 elsewhere / in Chrome.
export function readSafeAreaInsetTopPx(): number {
  if (typeof document === 'undefined' || !document.body) return 0
  const probe = document.createElement('div')
  probe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;padding-top:env(safe-area-inset-top,0px);'
  document.body.appendChild(probe)
  const px = Number.parseFloat(getComputedStyle(probe).paddingTop) || 0
  probe.remove()
  return px
}

export function readSafeAreaInsetBottomPx(): number {
  if (typeof document === 'undefined' || !document.body) return 0
  const probe = document.createElement('div')
  probe.style.cssText =
    'position:fixed;bottom:0;left:0;width:0;height:0;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom,0px);'
  document.body.appendChild(probe)
  const px = Number.parseFloat(getComputedStyle(probe).paddingBottom) || 0
  probe.remove()
  return px
}

// Max height for a mobile bottom sheet at its tallest snap, so the sheet's top stays a comfortable
// distance below the notch / Dynamic Island. `topPeekPx` is the bare strip of map wanted above the
// sheet; we additionally clear the top safe-area (the old `viewportHeight - 88` ignored it, so on a
// notched iPhone the sheet crowded the clock).
export function mobileSheetFullHeight(viewportHeight: number, topPeekPx = 88): number {
  return Math.max(0, viewportHeight - topPeekPx - readSafeAreaInsetTopPx())
}

function resolveHeaderClearance(baseHeight = DEFAULT_HEADER_HEIGHT, extraOffset = 0) {
  const clearancePx = `${baseHeight + extraOffset}px`
  return `calc(${clearancePx} + ${envInsets.top})`
}

export function useSafeAreaInsets() {
  return computed(() => envInsets)
}

export function useSafeAreaHeaderOffset(baseHeight = 48) {
  return computed(() => ({
    paddingTop: envInsets.top,
    minHeight: `calc(${baseHeight}px + ${envInsets.top})`,
  }))
}

export function useSafeAreaHeaderClearance(baseHeight = DEFAULT_HEADER_HEIGHT, extraOffset = 0) {
  return computed(() => resolveHeaderClearance(baseHeight, extraOffset))
}
